import pick from 'lodash/pick';
import { DateTime } from 'luxon';
import { getIdentifier, getStore } from '~/mst/utils';
import I18n from '~/utils/i18n';
import { parseTags } from '~/utils/tags';
import { ReportTypes, ReportRunTypes } from './types';
import type { IReportModel } from './model';

export default (self: IReportModel) => {
  return {
    get owner() {
      return getStore(self).organizations.getById(self.organization_id);
    },
    get creator() {
      return getStore(self).users.getById(self.user_id);
    },
    get chatId() {
      return `report-${getIdentifier(self)}-chat`;
    },
    // Only scheduled reports can be edited. Run-once reports are view-only and their
    // edit page is not reachable (the "Edit" action is hidden for them).
    get isEditable() {
      return self.run_type === ReportRunTypes.scheduled;
    },
    get searchString() {
      const searchFields = ['name', 'description'];
      return Object.values(pick(self, searchFields)).join(' ').toLowerCase();
    },
    get reportType() {
      const type = self.configuration?.type;
      return type === 'aqi_basic' || type === 'aqi_extended' ? ReportTypes.air_quality : type;
    },
    get reportTypeFilterTags() {
      return self.reportType ? [self.reportType] : [];
    },
    // One "Month YYYY" bucket for every calendar month the reporting period spans.
    get reportingPeriodFilterTags() {
      const from = self.configuration?.date_from;
      const to = self.configuration?.date_to;
      if (!from && !to) {
        return [];
      }
      const start = DateTime.fromISO((from ?? to) as string, { setZone: true }).startOf('month');
      const end = DateTime.fromISO((to ?? from) as string, { setZone: true }).startOf('month');
      if (!start.isValid || !end.isValid || end < start) {
        return [];
      }
      const labels: string[] = [];
      let cursor = start;
      // Cap iterations so a malformed range can't produce an unbounded list.
      for (let i = 0; i < 60 && cursor <= end; i += 1) {
        labels.push(cursor.toFormat('LLLL yyyy'));
        cursor = cursor.plus({ months: 1 });
      }
      return labels;
    },
    get tagsFilterTags() {
      return parseTags(self.description);
    },
    get filterTags() {
      return [...self.reportTypeFilterTags, ...self.reportingPeriodFilterTags, ...self.tagsFilterTags].map((tag) => tag.toLowerCase());
    },
    get jobStatusError(): string | null {
      if (!self.job_statuses) return null;

      const errors: string[] = [];

      const getJobLabel = (key: string): string => {
        const labels: Record<string, string> = {
          report_html_uploads: I18n.t('reports.job_statuses.html_upload_failed'),
          report_pdf_uploads: I18n.t('reports.job_statuses.pdf_upload_failed'),
          report_emails: I18n.t('reports.job_statuses.email_delivery_failed')
        };
        return labels[key] || I18n.t('reports.job_statuses.generic_failed', { key });
      };

      Object.entries(self.job_statuses).forEach(([key, status]) => {
        if (status?.state === 'failed') {
          const jobLabel = getJobLabel(key);
          // Handle error as object with message and status properties
          let errorDetail = '';
          if (status?.error) {
            if (typeof status.error === 'string') {
              errorDetail = status.error;
            } else if (status.error.message) {
              errorDetail = status.error.status ? `${status.error.message} (${status.error.status})` : status.error.message;
            }
          }
          const errorLine = errorDetail ? `${jobLabel}: ${errorDetail}` : jobLabel;
          errors.push(errorLine);
        }
      });

      return errors.length > 0 ? errors.join('\n') : null;
    },
    getAsset(periodKey: string | null) {
      return self.assets?.getAsset(periodKey) || null;
    },
    // Payload of the analytics export endpoint for a period (scheduled reports) or the report's own
    // dates, or null without dates (scheduled reports before their first run). Used for the XLSX
    // export, which isn't stored with the assets.
    exportPayload(periodKey: string | null) {
      const asset = self.assets?.getAsset(periodKey) || null;
      const { name, organization_id: organizationId, configuration } = self.toJSON() as any;
      const dateFrom = asset?.date_from || configuration?.date_from;
      const dateTo = asset?.date_to || configuration?.date_to;
      if (!dateFrom || !dateTo) return null;
      return { name, organization_id: organizationId, configuration: { ...configuration, date_from: dateFrom, date_to: dateTo } };
    }
  };
};
